import dayjs from "dayjs";
import { serverInfo } from "../config/serverConfig";
import { getMessageForFetchError } from "../utils/apiError";
import { apiFetch } from "./apiFetch";

async function readJsonOrText(resp) {
  const text = await resp.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

function errorFromResponse(data, fallback) {
  const msg =
    data && typeof data.message === "string" && data.message.trim()
      ? data.message.trim()
      : fallback;
  return new Error(msg);
}

/**
 * @param {{ account_id: string, participant_user_id: string }} params
 * @returns {Promise<object[]>}
 */
export async function fetchParticipantContactLogs({ account_id, participant_user_id }) {
  if (!account_id) throw new Error("account_id is required");
  if (!participant_user_id) throw new Error("participant_user_id is required");
  try {
    const resp = await apiFetch(serverInfo.serverUrl + "/getParticipantContactLogs", {
      method: "POST",
      body: JSON.stringify({ account_id, participant_user_id }),
    });
    const data = await readJsonOrText(resp);
    if (!resp.ok) throw errorFromResponse(data, "Unable to load contact notes.");
    return Array.isArray(data) ? data : [];
  } catch (err) {
    throw new Error(getMessageForFetchError(err));
  }
}

/**
 * Staff-wide contact-note / follow-up queue.
 * @param {{ account_id: string, scope: 'open'|'overdue'|'done'|'has_notes', as_of_date?: string }} params
 * @returns {Promise<object[]>}
 */
export async function fetchOpenParticipantContactFollowUps({
  account_id,
  scope = "open",
  as_of_date,
}) {
  if (!account_id) throw new Error("account_id is required");
  const asOf = as_of_date != null && String(as_of_date).trim()
    ? String(as_of_date).trim()
    : dayjs().format("YYYY-MM-DD");
  try {
    const resp = await apiFetch(serverInfo.serverUrl + "/getOpenParticipantContactFollowUps", {
      method: "POST",
      body: JSON.stringify({ account_id, scope, as_of_date: asOf }),
    });
    const data = await readJsonOrText(resp);
    if (!resp.ok) throw errorFromResponse(data, "Unable to load follow-ups.");
    return Array.isArray(data) ? data : [];
  } catch (err) {
    throw new Error(getMessageForFetchError(err));
  }
}

/**
 * @param {object} payload
 * @returns {Promise<object>}
 */
export async function createParticipantContactLog(payload) {
  try {
    const resp = await apiFetch(serverInfo.serverUrl + "/createParticipantContactLog", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    const data = await readJsonOrText(resp);
    if (!resp.ok) throw errorFromResponse(data, "Unable to add contact note.");
    return data;
  } catch (err) {
    throw new Error(getMessageForFetchError(err));
  }
}

/**
 * @param {object} payload
 * @returns {Promise<object>}
 */
export async function updateParticipantContactLog(payload) {
  try {
    const resp = await apiFetch(serverInfo.serverUrl + "/updateParticipantContactLog", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    const data = await readJsonOrText(resp);
    if (!resp.ok) throw errorFromResponse(data, "Unable to update contact note.");
    return data;
  } catch (err) {
    throw new Error(getMessageForFetchError(err));
  }
}

/**
 * @param {{ account_id: string, _id: string }} params
 * @returns {Promise<object>}
 */
export async function completeParticipantContactLogFollowUp({ account_id, _id }) {
  try {
    const resp = await apiFetch(serverInfo.serverUrl + "/completeParticipantContactLogFollowUp", {
      method: "POST",
      body: JSON.stringify({ account_id, _id }),
    });
    const data = await readJsonOrText(resp);
    if (!resp.ok) throw errorFromResponse(data, "Unable to close follow-up.");
    return data;
  } catch (err) {
    throw new Error(getMessageForFetchError(err));
  }
}

/**
 * @param {{ account_id: string, _id: string }} params
 * @returns {Promise<object>}
 */
export async function reopenParticipantContactLogFollowUp({ account_id, _id }) {
  try {
    const resp = await apiFetch(serverInfo.serverUrl + "/reopenParticipantContactLogFollowUp", {
      method: "POST",
      body: JSON.stringify({ account_id, _id }),
    });
    const data = await readJsonOrText(resp);
    if (!resp.ok) throw errorFromResponse(data, "Unable to reopen follow-up.");
    return data;
  } catch (err) {
    throw new Error(getMessageForFetchError(err));
  }
}

/**
 * @param {{ account_id: string, _id: string }} params
 */
export async function deleteParticipantContactLog({ account_id, _id }) {
  try {
    const resp = await apiFetch(serverInfo.serverUrl + "/deleteParticipantContactLog", {
      method: "POST",
      body: JSON.stringify({ account_id, _id }),
    });
    const data = await readJsonOrText(resp);
    if (!resp.ok) throw errorFromResponse(data, "Unable to delete contact note.");
    return data;
  } catch (err) {
    throw new Error(getMessageForFetchError(err));
  }
}
